import { captureException } from '@sentry/nextjs';

import { ApiError, getApiErrorBody } from '@/utils/api-error';
import { ENV } from '@/utils/env';
import debugLog from '@/utils/logs';

export type ErrorModule =
  | 'api-client'
  | 'auth'
  | 'utils'
  | 'actions'
  | 'tasks'
  | 'designs'
  | 'product-cards'
  | 'suppliers'
  | 'global'
  | 'unit-packagings'
  | 'orders'
  | 'customer-orders'
  | 'ports'
  | 'purchase-orders'
  | 'transports'
  | 'audit-log';

const isUnauthorizedError = (error: unknown): boolean => {
  const body = getApiErrorBody(error);

  return (
    typeof body === 'object' && body !== null && 'statusCode' in body && body.statusCode === 401
  );
};

// Sentry drops custom Error props, so the backend payload is copied into `extra` by hand.
// The `response` prefix keeps them apart from `meta` — `meta.message` already holds the
// frontend context message. The backend is inconsistent between `error` and `errors`, so both are
// forwarded. `errors` (Zod issues) is stringified: Sentry's default `normalizeDepth` of 3 would
// collapse each issue's `path` to "[Array]".
const getApiResponseExtra = (error: unknown) => {
  if (!(error instanceof ApiError) || typeof error.body !== 'object' || error.body === null) {
    return {};
  }

  const { body } = error;

  return {
    ...('statusCode' in body && { responseStatus: body.statusCode }),
    ...('message' in body && { responseMessage: body.message }),
    ...('error' in body && { responseError: body.error }),
    ...('errors' in body && { responseErrors: JSON.stringify(body.errors) }),
  };
};

export const errorReport = (
  error: unknown,
  meta?: { message?: string; module?: ErrorModule } & Record<string, unknown>,
) => {
  // Don't report 401 errors to Sentry - auth flow handles them
  if (isUnauthorizedError(error)) {
    if (ENV.NODE_ENV === 'development') {
      debugLog('log', 'Skipping 401 error report', error, meta);
    }
    return;
  }

  if (ENV.NODE_ENV === 'development') {
    debugLog('error', error, meta);
  }

  captureException(error, { extra: { ...meta, ...getApiResponseExtra(error) } });
};
